import * as L from 'leaflet';
import 'leaflet.markercluster';
import MiniMap from 'leaflet-minimap';
import Tagify from '@yaireo/tagify';
import debounce from 'lodash-es/debounce';

import {
    clusterOptions,
    getTagifyOptions,
    initialState,
    mapOptions,
    minimapOptions,
    miniMapStyles,
    stateMapStyles,
    spinner,
    resetFilterBtn,
    resetMapBtn,
    searchInput,
    tagInput,
    projectInputs,
    groupInputs,
    allProjects,
    allGroups,
    getApiUrl,
} from './options';
import {
    checkMap,
    checkTooltips,
    clearMarkers,
    getData,
    resetMap,
    setFilter,
    setMarkers,
} from './lib';
import { initSupportLayer, initSchoolLayer } from './layers';

export const initMap = async function () {
    // Get geojson data
    const baseUrl = getApiUrl();
    const states = await getData('../geojson/landkreise.json');
    const support = await getData(`${baseUrl}/api/public/support`);
    const schools = await getData(`${baseUrl}/api/public/schools`);

    // Initialize app state
    let state = initialState;

    // Generate tags from data, init tag filter
    const allTags = tagInput.dataset.alltags.split(',');
    const tagify = new Tagify(tagInput, getTagifyOptions(allTags));

    // Init map
    let map = L.map('map', mapOptions);
    map.setMaxBounds(map.getBounds().pad(0.45));
    spinner.remove();

    // States layer
    L.geoJSON(states, { style: stateMapStyles }).addTo(map);

    // Support layer
    const supportLayer = initSupportLayer(map, state);
    let supportMarkers;
    state.clustering.support
        ? (supportMarkers = L.markerClusterGroup(clusterOptions))
        : (supportMarkers = undefined);

    // School layer
    const schoolLayer = initSchoolLayer(map, state);
    let schoolMarkers;
    state.clustering.schools
        ? (schoolMarkers = L.markerClusterGroup(clusterOptions))
        : (schoolMarkers = undefined);
    setMarkers(map, schoolLayer, schools, schoolMarkers);

    setFilter(state, tagify);

    if (window.matchMedia('(min-width: 992px)').matches) {
        // Minimap layer
        const miniStates = new L.geoJSON(states, { style: miniMapStyles });
        new MiniMap(miniStates, minimapOptions).addTo(map);
    }

    // Set markers in a specific layer
    function setLayer(layer, data, markers) {
        clearMarkers(layer, markers);
        setFilter(state, tagify);
        setMarkers(map, layer, data, markers);
    }

    // Reset filters
    function resetFilter() {
        state = initialState;

        searchInput.value = '';
        tagify.removeAllTags();
        for (let projectCheckbox of projectInputs) {
            projectCheckbox.checked = true;
        }
        for (let groupCheckbox of groupInputs) {
            groupCheckbox.checked = false;
        }

        clearMarkers(supportLayer, supportMarkers);
        setLayer(schoolLayer, schools, schoolMarkers);

        resetFilterBtn.disabled = true;
    }

    // Filter events
    function handleSearchInput() {
        setLayer(supportLayer, support, supportMarkers);
        setLayer(schoolLayer, schools, schoolMarkers);
    }

    searchInput.addEventListener('input', debounce(handleSearchInput, 1000));

    tagInput.addEventListener('change', () => {
        setLayer(schoolLayer, schools, schoolMarkers);
    });

    for (let projectCheckbox of projectInputs) {
        // Project checkboxes
        projectCheckbox.onchange = () => {
            setLayer(schoolLayer, schools, schoolMarkers);
        };
    }
    for (let groupCheckbox of groupInputs) {
        // Group checkboxes
        groupCheckbox.onchange = () => {
            setLayer(supportLayer, support, supportMarkers);
        };
    }
    // Catch-all checkboxes
    allProjects.onchange = (e) => {
        for (let projectCheckbox of projectInputs) {
            projectCheckbox.checked = e.target.checked;
        }

        if (e.target.checked) {
            setLayer(schoolLayer, schools, schoolMarkers);
        } else {
            clearMarkers(schoolLayer, schoolMarkers);
            state.selected.projects = [];
            setFilter(state, tagify);
        }
    };
    allGroups.onchange = (e) => {
        for (let groupCheckbox of groupInputs) {
            groupCheckbox.checked = e.target.checked;
        }

        if (e.target.checked) {
            setLayer(supportLayer, support, supportMarkers);
        } else {
            clearMarkers(supportLayer, supportMarkers);
            state.selected.groups = [];
            setFilter(state, tagify);
        }
    };

    // Map events
    map.on('moveend', () => checkMap(map));
    map.on('zoomend', () => checkTooltips(map));

    // Button events
    resetMapBtn.addEventListener('click', () => resetMap(map));
    resetFilterBtn.addEventListener('click', resetFilter);
};
